/**
 * /akun/tentang — About app (Phase 5.4).
 *
 * Static info page. Shows:
 *  - App name + version
 *  - Build number
 *  - Privacy policy link
 *  - Terms of use link
 *  - Contact email
 *  - Credits
 *
 * All data is hardcoded Y1 (no backend lookup). Y2: add
 * "What's new" + changelog pulled from /v1/changelog.
 */

import { useRouter } from 'expo-router';
import { Linking, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { AppIcon } from '@/components/app-icon';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { MISC_ICON } from '@/lib/icons';
import { APP_VERSION } from '@funpaper/shared';

const CONTACT_EMAIL = 'terusmengajar@gmail.com';

export default function TentangScreen() {
  const router = useRouter();

  const sendEmail = () => Linking.openURL(`mailto:${CONTACT_EMAIL}`).catch(() => {});

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.back()}
            style={({ pressed }) => [styles.backBtn, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Kembali"
          >
            <Text style={styles.backArrow}>←</Text>
          </Pressable>
          <ThemedText type="subtitle" style={styles.headerTitle}>Tentang Aplikasi</ThemedText>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView contentContainerStyle={styles.scrollContent}>
          {/* Hero */}
          <View style={styles.hero}>
            <AppIcon source={MISC_ICON.books} size={64} />
            <ThemedText type="title" style={styles.heroTitle}>Funpaper</ThemedText>
            <ThemedText type="default" themeColor="textSecondary" style={styles.heroSubtitle}>
              Ide aktivitas harian untuk orang tua di Indonesia
            </ThemedText>
            <Text style={styles.versionLine}>Versi {APP_VERSION}</Text>
          </View>

          {/* Links */}
          <View style={styles.section}>
            <LinkRow
              label="Kebijakan Privasi"
              subtitle="Bagaimana kami melindungi data orang tua dan anak"
              onPress={() => router.push('/akun/kebijakan-privasi' as never)}
            />
            <LinkRow
              label="Syarat Penggunaan"
              subtitle="Ketentuan penggunaan aplikasi"
              onPress={() => router.push('/akun/syarat-penggunaan' as never)}
            />
            <LinkRow
              label="Hubungi Kami"
              subtitle={CONTACT_EMAIL}
              onPress={sendEmail}
            />
          </View>

          {/* Credits */}
          <View style={styles.credits}>
            <Text style={styles.creditsText}>
              Funpaper dibuat oleh tim Terus Mengajar.
            </Text>
            <Text style={styles.creditsText}>
              © 2026 Terus Mengajar. Semua hak dilindungi.
            </Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    </ThemedView>
  );
}

function LinkRow({ label, subtitle, onPress }: {
  label: string;
  subtitle: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.linkRow, pressed && styles.btnPressed]}
      accessibilityRole="link"
      accessibilityLabel={label}
    >
      <View style={styles.linkContent}>
        <ThemedText type="smallBold" style={styles.linkLabel}>{label}</ThemedText>
        <ThemedText type="small" themeColor="textSecondary">{subtitle}</ThemedText>
      </View>
      <Text style={styles.chev}>›</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
  },
  backBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backArrow: { fontSize: 24, color: Brand.primary, fontWeight: '600' },
  headerTitle: { flex: 1, textAlign: 'center', fontSize: 18, fontWeight: '700', color: '#1A0B5C' },
  scrollContent: { padding: Spacing.four, paddingBottom: Spacing.five },
  hero: { alignItems: 'center', paddingVertical: Spacing.five, gap: Spacing.two },
  heroTitle: { fontSize: 28, fontWeight: '700', color: '#1A0B5C' },
  heroSubtitle: { fontSize: 14, textAlign: 'center', maxWidth: 280 },
  versionLine: { fontSize: 12, color: '#9CA3AF', fontWeight: '600', marginTop: Spacing.two },
  section: { gap: Spacing.two, marginTop: Spacing.three },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    padding: Spacing.three,
    gap: Spacing.three,
  },
  linkContent: { flex: 1 },
  linkLabel: { color: '#1A0B5C' },
  chev: { fontSize: 24, color: '#9CA3AF', fontWeight: '300' },
  credits: { alignItems: 'center', marginTop: Spacing.five, gap: Spacing.one },
  creditsText: { fontSize: 12, color: '#9CA3AF', textAlign: 'center' },
  btnPressed: { opacity: 0.7 },
});
